import { Box } from '@mui/material';
import type { QuestionRegion } from '@/types/pipeline';
import { getRegionDisplayColors, HIGHLIGHT_STROKE } from '@/lib/regionColors';

export function ImageWithRegions({
  src,
  regions,
  highlightIndex,
  showCoordinates = false,
  onRegionHoverEnter,
  onRegionHoverLeave,
}: {
  src: string;
  regions: QuestionRegion[];
  highlightIndex?: number;
  showCoordinates?: boolean;
  onRegionHoverEnter?: (questionId: string, index: number) => void;
  onRegionHoverLeave?: () => void;
}) {
  const isAnswerElement = (region: QuestionRegion): boolean => region.type?.startsWith('answer-') ?? false;

  const baseColors = getRegionDisplayColors(regions);
  const regionColors = baseColors.map((c, index) => (index === highlightIndex ? HIGHLIGHT_STROKE : c));

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <Box component="img" src={src} alt="試卷" sx={{ width: '100%', borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
      <Box
        component="svg"
        viewBox="0 0 1 1"
        preserveAspectRatio="none"
        sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
      >
        {showCoordinates && (
          <g pointerEvents="none">
            <text x={0.01} y={0.025} fill="#666" fontSize={0.018} fontFamily="monospace">(0, 0)</text>
            <text x={0.99} y={0.025} fill="#666" fontSize={0.018} fontFamily="monospace" textAnchor="end">(1, 0)</text>
            <text x={0.01} y={0.99} fill="#666" fontSize={0.018} fontFamily="monospace">(0, 1)</text>
            <text x={0.99} y={0.99} fill="#666" fontSize={0.018} fontFamily="monospace" textAnchor="end">(1, 1)</text>
            {[0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9].map((tick) => (
              <g key={`top-${tick}`}>
                <line x1={tick} y1={0} x2={tick} y2={0.015} stroke="#999" strokeWidth={0.002} />
                <text x={tick} y={0.028} fill="#999" fontSize={0.012} fontFamily="monospace" textAnchor="middle">{tick.toFixed(1)}</text>
              </g>
            ))}
            {[0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9].map((tick) => (
              <g key={`left-${tick}`}>
                <line x1={0} y1={tick} x2={0.012} y2={tick} stroke="#999" strokeWidth={0.002} />
                <text x={0.016} y={tick + 0.005} fill="#999" fontSize={0.012} fontFamily="monospace">{tick.toFixed(1)}</text>
              </g>
            ))}
          </g>
        )}

        {regions.map((region, index) => {
          const { x, y, width, height } = region.bbox;
          const color = regionColors[index];
          const isHighlighted = index === highlightIndex;
          const x2 = x + width;
          const y2 = y + height;
          const isSectionInstruction = region.type === 'section-instruction';
          const isSectionContent = region.type === 'section-content';
          const isAnswer = isAnswerElement(region);

          let labelWidth = 0.08;
          if (isSectionInstruction) labelWidth = 0.12;
          else if (isSectionContent) labelWidth = 0.14;
          else if (isAnswer) labelWidth = 0.16;

          let fillColor = 'rgba(0,0,0,0)';
          let strokeWidth = 0.003;
          let strokeDasharray: string | undefined = undefined;
          let labelIcon = '';
          let labelText = region.questionId;

          if (isHighlighted) {
            fillColor = 'rgba(244, 67, 54, 0.15)'; // Red highlight fill matching HIGHLIGHT_STROKE
          } else if (isSectionInstruction) {
            fillColor = 'rgba(198, 40, 40, 0.08)';
            strokeWidth = 0.004;
            strokeDasharray = '0.01 0.005';
            labelIcon = '📋 ';
          } else if (isSectionContent) {
            fillColor = 'rgba(93, 64, 55, 0.06)';
            strokeWidth = 0.003;
            strokeDasharray = '0.006 0.004';
            labelIcon = '📖 ';
          } else if (region.type === 'answer-text') {
            fillColor = 'rgba(46, 125, 50, 0.12)';
            strokeWidth = 0.002;
            labelIcon = '✏️ ';
            if (region.transcription) {
              labelText = region.transcription.slice(0, 8) + (region.transcription.length > 8 ? '…' : '');
            }
          } else if (region.type === 'answer-mc') {
            fillColor = 'rgba(21, 101, 192, 0.15)';
            strokeWidth = 0.002;
            labelIcon = '⭕ ';
            if (region.selectedOption) labelText = `選: ${region.selectedOption}`;
          } else if (region.type === 'answer-symbol') {
            fillColor = 'rgba(245, 124, 0, 0.12)';
            strokeWidth = 0.002;
            labelIcon = '✓ ';
            if (region.symbolType) labelText = region.symbolType;
          } else if (region.type === 'answer-drawing') {
            fillColor = 'rgba(123, 31, 162, 0.10)';
            strokeWidth = 0.002;
            strokeDasharray = '0.004 0.002';
            labelIcon = '🎨 ';
            labelText = '圖';
          }

          return (
            <g
              key={`${region.questionId}-${index}`}
              onMouseEnter={() => onRegionHoverEnter?.(region.questionId, index)}
              onMouseLeave={() => onRegionHoverLeave?.()}
              style={{ cursor: onRegionHoverEnter ? 'pointer' : 'default', pointerEvents: 'all' }}
            >
              <rect x={x} y={y} width={width} height={height} fill={fillColor} stroke={color} strokeWidth={strokeWidth} strokeDasharray={strokeDasharray} />
              <rect x={x} y={y - (isAnswer ? 0.018 : 0.024)} width={Math.min(labelWidth, width)} height={isAnswer ? 0.018 : 0.024} fill={color} rx={isAnswer ? 0.003 : 0} />
              <text x={x + 0.004} y={y - (isAnswer ? 0.004 : 0.007)} fill="#fff" fontSize={isAnswer ? 0.010 : (isSectionInstruction || isSectionContent ? 0.012 : 0.014)} fontFamily="sans-serif" fontWeight="bold">
                {labelIcon}{labelText}
              </text>
              {showCoordinates && !isAnswer && (
                <>
                  <text x={x + 0.003} y={y + 0.018} fill={color} fontSize={0.012} fontFamily="monospace" fontWeight="bold">
                    ({x.toFixed(2)}, {y.toFixed(2)})
                  </text>
                  <text x={x2 - 0.003} y={y2 - 0.006} fill={color} fontSize={0.012} fontFamily="monospace" fontWeight="bold" textAnchor="end">
                    ({x2.toFixed(2)}, {y2.toFixed(2)})
                  </text>
                </>
              )}
            </g>
          );
        })}
      </Box>
    </Box>
  );
}
